import React, { useEffect, useLayoutEffect, useState } from 'react';
import "../Css/ProductPage.css"; // Assuming this CSS handles existing styles
import { FaHome, FaSearch } from 'react-icons/fa';
import { IoIosArrowForward } from 'react-icons/io';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Footer from '../Component/Footer/Footer';
import axios from 'axios';
import product_image_not_found from "../Images/product_image_not_found.jpg";
import product_not_found_image from "../Images/product_not_found.avif";
import { HiMenuAlt1 } from 'react-icons/hi'; // Hamburger icon for mobile filter
import { Drawer } from 'antd'; // Ant Design Drawer for mobile filters
import InputGroup from 'react-bootstrap/InputGroup';

const ProductPage = () => {
  // State to hold all products fetched from the API
  const [allProducts, setAllProducts] = useState([]);
  // State to hold products after applying filters, which will be displayed
  const [filteredProducts, setFilteredProducts] = useState([]);
  // States for categories
  const [mainCategories, setMainCategories] = useState([]);
  // States for selected filter criteria
  const [selectedMainCategory, setSelectedMainCategory] = useState(''); // Stores slug
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(''); // Stores slug
  // State to store unique brands extracted from product data
  const [uniqueBrands, setUniqueBrands] = useState([]);
  // Loading state for API calls
  const [loading, setLoading] = useState(false);
  // State to toggle the mobile filter drawer
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const navigate = useNavigate();
  // State for search term
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();


  // Scroll to top on component mount
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function to fetch all products from the API
  const handleFetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (minPrice) params.append('min_price', minPrice); // Changed to min_price
      if (maxPrice) params.append('max_price', maxPrice); // Changed to max_price
      // Use selectedMainCategory (which is a slug) for the 'sub_category_slug' parameter
      if (selectedMainCategory) params.append('sub_category_slug', selectedMainCategory); // Changed to sub_category_slug
      // Use selectedBrand (which is a slug) for the 'brand_slug' parameter
      if (selectedBrand) params.append('brand_slug', selectedBrand); // Changed to brand_slug

      const resp = await axios.get(`https://api.woodsy.co.in/api/wood-images-product?${params.toString()}`);
      console.log("resp",resp)
      if (resp) {
        setAllProducts(resp?.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch main categories
  const fetchMainCategories = async () => {
    try {
      const resp = await axios.get(`https://api.woodsy.co.in/api/subcategories`);
      if (resp?.data) {
        setMainCategories(resp.data);
      }
    } catch (error) {
      console.error("Error fetching main categories:", error);
    }
  };

  // Function to fetch brands
  const fetchBrand = async () => {
    try {
      const resp = await axios.get(`https://api.woodsy.co.in/api/brands`);
      if (resp?.data) {
        setUniqueBrands(resp.data.data);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  // Initial data fetching on component mount
  useEffect(() => {
    fetchMainCategories();
    fetchBrand();
  }, []); // Empty dependency array ensures this runs only once

  // Fetch products whenever filters change
  useEffect(() => {
    handleFetchProducts();
  }, [searchTerm, minPrice, maxPrice, selectedMainCategory, selectedBrand]);

  // Effect to apply filters whenever filter criteria or products change
  useEffect(() => {
    setFilteredProducts(allProducts);
  }, [allProducts]); // Only depends on allProducts, as filtering is server-side

  // Helper to parse query params from URL
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get('search') || '',
      minPrice: params.get('min_price') || '', // Changed to min_price
      maxPrice: params.get('max_price') || '', // Changed to max_price
      category: params.get('sub_category_slug') || '', // Changed to sub_category_slug
      brand: params.get('brand_slug') || '',     // Changed to brand_slug
    };
  };

  // Sync state with query params on mount/location change
  useEffect(() => {
    const { search, minPrice, maxPrice, category, brand } = getQueryParams();
    setSearchTerm(search);
    setMinPrice(minPrice);
    setMaxPrice(maxPrice);
    setSelectedMainCategory(category); // Set state with slug from URL
    setSelectedBrand(brand);         // Set state with slug from URL
  }, [location.search]);

  // Helper to build query string from current state
  const buildQuery = (overrides = {}) => {
    const params = new URLSearchParams();
    const s = overrides.search !== undefined ? overrides.search : searchTerm;
    const min = overrides.minPrice !== undefined ? overrides.minPrice : minPrice;
    const max = overrides.maxPrice !== undefined ? overrides.maxPrice : maxPrice;
    const cat = overrides.category !== undefined ? overrides.category : selectedMainCategory;
    const br = overrides.brand !== undefined ? overrides.brand : selectedBrand;

    if (s) params.set('search', s);
    if (min) params.set('min_price', min); // Changed to min_price
    if (max) params.set('max_price', max); // Changed to max_price
    if (cat) params.set('sub_category_slug', cat); // Changed to sub_category_slug
    if (br) params.set('brand_slug', br);     // Changed to brand_slug
    return params.toString();
  };

  // Handlers for submit/search icons
  const handleSearchSubmit = () => {
    navigate(`/product?${buildQuery({ search: searchTerm })}`);
  };
  const handlePriceSubmit = () => {
    navigate(`/product?${buildQuery({ minPrice, maxPrice })}`);
  };

  // Update category and brand selection to immediately update URL
  const handleCategorySelect = (slug) => { // Expects slug as argument
    const newCategory = slug === selectedMainCategory ? '' : slug;
    setSelectedMainCategory(newCategory);
    navigate(`/product?${buildQuery({ category: newCategory })}`);
  };
  const handleBrandSelect = (slug) => { // Expects slug as argument
    const newBrand = slug === selectedBrand ? '' : slug;
    setSelectedBrand(newBrand);
    navigate(`/product?${buildQuery({ brand: newBrand })}`);
  };

  // Function to clear all applied filters
  const handleClearFilters = () => {
    setSelectedMainCategory('');
    setMinPrice('');
    setMaxPrice('');
    setSelectedBrand('');
    setSearchTerm(''); // Clear search term
    navigate('/product'); // Clear all query params from URL
  };

  // Handler for search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Functions to control the mobile filter drawer
  const showDrawer = () => {
    setShowFilterDrawer(true);
  };

  const onCloseDrawer = () => {
    setShowFilterDrawer(false);
  };

  return (
    <div>
      {/* Product Banner Section */}
      <section className='Product-section-banner'>
        <div className='container'>
          <div className='Product-section-banner-main-con'>
            <div className='Product-section-banner-con'>
              <h2>Product</h2>
              <div className='Product-section-banner-content'>
                <FaHome />
                <IoIosArrowForward />
                <span>Product</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area: Filters and Product Grid */}
      <div className='product-page-main-con container py-4'> {/* Added container and padding */}
        <div className='row'>
          {/* Filter Section (Desktop View) */}
          {/* Hidden on small screens (d-none) and visible on medium screens and up (d-md-block) */}
          <div className='col-md-3 d-none d-md-block'>
            <div className='product-filter-sidebar p-3 border rounded shadow-sm'> {/* Added shadow for better appearance */}
              <h4 className='product-page-filter-head'>Filters</h4>
              <hr className="mb-4" /> {/* Separator */}

              {/* search by name  */}
              <div className='product-page-filter-main-con mb-3'>
                <div className='product-page-subtitle'>
                  <h3>Search Product Name</h3>
                </div>
                <div className='product-filter-search-here'>
                  <InputGroup className='filter-inputs-search-main-con'>
                    <Form.Control
                      type="text"
                      placeholder="Search by product name..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="rounded-pill search-input-main-con"
                      onKeyDown={e => { if (e.key === 'Enter') handleSearchSubmit(); }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={handleSearchSubmit}
                      className='search-input-main-con-btn'
                    >
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </div>
              </div>
              {/* Price Range Filter */}
              <Form.Group className="main-con-for-filter mb-3">
                <Form.Label className="product-page-subtitle">Price Range</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="number"
                    placeholder="Min Price"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="me-2 rounded-pill"
                  />
                  <span className="mx-1">-</span>
                  <Form.Control
                    type="number"
                    placeholder="Max Price"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="rounded-pill"
                  />
                  <Button
                    variant="outline-secondary"
                    className="ms-2 d-flex align-items-center justify-content-center"
                    onClick={handlePriceSubmit}
                    style={{ borderRadius: '50%', width: '40px', height: '40px' }}
                  >
                    <FaSearch />
                  </Button>
                </div>
              </Form.Group>

              {/* filter main category  */}
              <div className='product-page-filter-main-con mb-3'>
                <div className='product-page-subtitle'>
                  <h3>Main Category</h3>
                </div>
                <div className='product-page-filter-category-main-con d-flex flex-wrap'>
                  {mainCategories.map((item) => (
                    <div
                      className={`product-page-filter-category-card-con ${selectedMainCategory === item.slug ? 'selectedcard' : ''}`}
                      key={item._id}
                      onClick={() => handleCategorySelect(item.slug)} // Pass slug
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='product-page-filter-category-car-image-con'>
                        <img src={item.image} alt={item.name} />
                      </div>
                      <h4>{item.name}</h4>
                    </div>
                  ))}
                </div>
              </div>
              {/* filter by Brand  */}
              <div className='product-page-filter-main-con mb-4'>
                <div className='product-page-subtitle'>
                  <h3>Select Brand</h3>
                </div>
                <div className='product-page-filter-category-main-con d-flex flex-wrap'>
                  {uniqueBrands.map((item) => (
                    <div
                      className={`product-page-filter-category-card-con ${selectedBrand === item.slug ? 'selectedcard' : ''}`}
                      key={item._id}
                      onClick={() => handleBrandSelect(item.slug)} // Pass slug
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='product-page-filter-brand-car-image-con'>
                        <img src={item.image} alt={item.name} />
                      </div>
                      <h4>{item.name}</h4>
                    </div>
                  ))}
                </div>
              </div>

              {/* Clear Filters Button */}
              <Button
                variant="outline-danger" // Changed to danger for emphasis
                onClick={handleClearFilters}
                className="w-100 mt-3 rounded-pill" // Added rounded-pill
              >
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Products Display Section */}
          <div className='col-md-9 col-12'>
            {/* Mobile Filter Button (Visible only on small screens) */}
            <div className="d-md-none mb-3">
              <Button
                variant="primary"
                onClick={showDrawer}
                className="w-100 rounded-pill shadow-sm" // Added rounded-pill and shadow
              >
                <HiMenuAlt1 className="me-2" /> Show Filters
              </Button>
            </div>

            <section className='product-section-2'>
              <div className='product-section-main-con'>
                {/* Loading Indicator */}
                {loading && (
                  <div className='text-center my-5'>
                    <h4>Loading products...</h4>
                    {/* You can add a spinner here if you have one */}
                  </div>
                )}

                {/* Display Filtered Products */}
                {!loading && filteredProducts.length > 0 && (
                  <div className='row'>
                    {filteredProducts.map((item) => {
                      const { product, price, images } = item;
                      const productName = product?.name || "Product Name";
                      const productSlug = product?.slug || "#";
                      // Use placeholder image if actual image is not found
                      const firstImage = images?.[0] || product_image_not_found;
                      const secondImage = images?.[1] || firstImage; // Fallback to first image

                      const mainPrice = price?.[0]?.price;
                      return (
                        <div className='col-lg-4 col-md-6 col-sm-6 col-12 mb-4' key={product._id}> {/* Adjusted columns for responsiveness */}
                          <div className='home-trending-section-card-body-con border rounded shadow-sm h-100 d-flex flex-column'> {/* Added styling for cards */}
                            <Link to={`/product-details/${productSlug}`} className="text-decoration-none text-dark">
                              <div className='product-wrapper imgloaded'>
                                <div className='thumbs product-thumb'>
                                  <div className='view-image fade_img'>
                                    <div className='grid-view-item__link'>
                                      <img
                                        className='image_thumb_swap img-fluid rounded-top' // Added img-fluid for responsiveness
                                        src={secondImage}
                                        alt={productName}
                                        onError={(e) => (e.target.src = product_image_not_found)} // Fallback on error
                                      />
                                      <img
                                        className='grid-view-item__image featured-image image_thumb img-fluid rounded-top' // Added img-fluid for responsiveness
                                        src={firstImage}
                                        alt={productName}
                                        onError={(e) => (e.target.src = product_image_not_found)} // Fallback on error
                                      />
                                    </div>
                                  </div>
                                  <div className='home-trending-section-car-details'> {/* Added padding */}
                                    <h3 className="product-page-subtitle mb-2">{productName}</h3> {/* Styled product name */}
                                    <h4></h4>
                                    {mainPrice ? (
                                      <p className="mb-0">
                                        <span className="text-success fs-6 fw-bold">₹ {parseFloat(mainPrice).toFixed(2)}</span> / unit
                                        <span className='discount-percentage text-danger ms-2'> -30%</span> {/* Styled discount */}
                                        <s className='product-price__price compare_price text-muted ms-2'>
                                          <span className='money'>₹ {(parseFloat(mainPrice) * 1.3).toFixed(2)}</span>
                                        </s>
                                      </p>
                                    ) : (
                                      <p className="text-warning mb-0">Price Not Available</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* No Products Found / No Products Match Filters Messages */}
                {!loading && allProducts.length > 0 && filteredProducts.length === 0 && (
                  <div className='product-not-found-main-image-con-div text-center py-5'>
                    <div className='product-not-found-main-image-con'>
                      <div className='product-not-found-main-image'>
                        <img src={product_not_found_image} alt='product_not_found_image' className="img-fluid" style={{ maxWidth: '300px' }} />
                      </div>
                    </div>
                    <h4 className="mt-4 text-muted">No products match your current filters.</h4>
                    <Button variant="link" onClick={handleClearFilters}>Clear Filters</Button>
                  </div>
                )}
                {!loading && allProducts.length === 0 && (
                  <div className='product-not-found-main-image-con-div text-center py-5'>
                    <div className='product-not-found-main-image-con'>
                      <div className='product-not-found-main-image'>
                        <img src={product_not_found_image} alt='product_not_found_image' className="img-fluid" style={{ maxWidth: '300px' }} />
                      </div>
                    </div>
                    <h4 className="mt-4 text-muted">No products found. Please try again later.</h4>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />

      {/* Mobile Filter Drawer */}
      <Drawer
        title="Filters"
        placement="right"
        onClose={onCloseDrawer}
        open={showFilterDrawer}
        width={300}
        footer={
          <Button
            variant="outline-danger"
            onClick={() => { handleClearFilters(); onCloseDrawer(); }}
            className="w-100 rounded-pill"
          >
            Clear Filters
          </Button>
        }
      >
        <div className='product-filter-sidebar p-3'>
          <h4 className='product-page-filter-head'>Filters</h4>
          <hr className="mb-4" />

          {/* Search by name for mobile */}
          <div className='product-page-filter-main-con mb-3'>
            <div className='product-page-subtitle'>
              <h3>Search Product Name</h3>
            </div>
            <div className='product-filter-search-here'>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by product name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="rounded-pill"
                  onKeyDown={e => { if (e.key === 'Enter') handleSearchSubmit(); }}
                />
                <Button
                  variant="outline-secondary"
                  onClick={handleSearchSubmit}
                  style={{ borderTopRightRadius: '50px', borderBottomRightRadius: '50px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                >
                  <FaSearch />
                </Button>
              </InputGroup>
            </div >
          </div >
          {/* Price Range Filter for mobile */}
          <Form.Group className="main-con-for-filter mb-3">
            <Form.Label className="product-page-subtitle">Price Range</Form.Label>
            <div className="d-flex align-items-center">
              <Form.Control
                type="number"
                placeholder="Min Price"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="me-2 rounded-pill"
              />
              <span className="mx-1">-</span>
              <Form.Control
                type="number"
                placeholder="Max Price"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="rounded-pill"
              />
              <Button
                variant="outline-secondary"
                className="ms-2 d-flex align-items-center justify-content-center"
                onClick={handlePriceSubmit}
                style={{ borderRadius: '50%', width: '40px', height: '40px' }}
              >
                <FaSearch />
              </Button>
            </div>
          </Form.Group>

          {/* Main Category Filter for mobile */}
          <div className='product-page-filter-main-con mb-3'>
            <div className='product-page-subtitle'>
              <h3>Main Category</h3>
            </div>
            <div className='product-page-filter-category-main-con d-flex flex-wrap'>
              {mainCategories.map((item) => (
                <div
                  className={`product-page-filter-category-card-con ${selectedMainCategory === item.slug ? 'selectedcard' : ''}`}
                  key={item._id}
                  onClick={() => handleCategorySelect(item.slug)} // Pass slug
                  style={{ cursor: 'pointer' }}
                >
                  <div className='product-page-filter-category-car-image-con'>
                    <img src={item.image} alt={item.name} />
                  </div>
                  <h4>{item.name}</h4>
                </div>
              ))}
            </div>
          </div>
          {/* Brand Filter for mobile */}
          <div className='product-page-filter-main-con mb-4'>
            <div className='product-page-subtitle'>
              <h3>Select Brand</h3>
            </div>
            <div className='product-page-filter-category-main-con d-flex flex-wrap'>
              {uniqueBrands.map((item) => (
                <div
                  className={`product-page-filter-category-card-con ${selectedBrand === item.slug ? 'selectedcard' : ''}`}
                  key={item._id}
                  onClick={() => handleBrandSelect(item.slug)} // Pass slug
                  style={{ cursor: 'pointer' }}
                >
                  <div className='product-page-filter-brand-car-image-con'>
                    <img src={item.image} alt={item.name} />
                  </div>
                  <h4>{item.name}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default ProductPage;